/**
 * Pure utilities for AI chat messages: turn raw AI SDK message parts into
 * display segments (text, tools, spec). Handles both dynamic-tool and
 * static (tool-{name}) parts so tool-call UI works with Node AI SDK or
 * pydantic-ai backends.
 */
import type { DynamicToolUIPart } from "ai";

export interface ToolSegmentEntry {
  toolCallId: string;
  toolName: string;
  state: string;
  input?: unknown;
  output?: unknown;
}

export type MessageSegment =
  | { kind: "text"; text: string }
  | { kind: "tools"; tools: ToolSegmentEntry[] }
  /**
   * `specIndex` defined → json-render flat-spec block (index = arrival order among flat data-spec parts).
   * `specIndex` undefined → legacy json-render patch path.
   */
  | { kind: "spec"; spec: unknown; specIndex?: number };

/** Raw AI SDK message part (discriminated by `type`). */
export interface MessagePart extends Record<string, unknown> {
  type: string;
}

const stripToolTags = (src: string): string =>
  src
    .replace(/<tool_call>[\s\S]*?<\/tool_call>/g, "")
    .replace(/<tool_response>[\s\S]*?<\/tool_response>/g, "")
    .trim();

const isDynamicToolPart = <T extends { type: string }>(
  part: T,
): part is T & DynamicToolUIPart =>
  part.type === "dynamic-tool" &&
  "toolName" in part &&
  typeof (part as Record<string, unknown>).toolName === "string" &&
  "toolCallId" in part &&
  typeof (part as Record<string, unknown>).toolCallId === "string" &&
  "state" in part &&
  typeof (part as Record<string, unknown>).state === "string";

const isStaticToolPart = (part: MessagePart): boolean =>
  part.type.startsWith("tool-") &&
  "toolCallId" in part &&
  typeof part.toolCallId === "string" &&
  "state" in part &&
  typeof part.state === "string";

const isToolPart = (part: MessagePart): boolean =>
  isDynamicToolPart(part) || isStaticToolPart(part);

const getToolName = (part: MessagePart): string => {
  if (isDynamicToolPart(part)) {
    return (part as DynamicToolUIPart).toolName;
  }
  return part.type.slice("tool-".length);
};

/**
 * Converts raw message parts into an ordered list of display segments.
 * Consecutive tool parts are merged into one "tools" segment.
 * - Flat specs (`data.type === "flat"`): one segment per render_spec call, placed
 *   inline by arrival position (`specIndex`). The spec payload is embedded directly.
 * - Legacy patch path (bare `data-spec`): at most one segment (backwards compat).
 */
export const buildMessageSegments = (
  parts: ReadonlyArray<MessagePart>,
  specPartType: string,
): MessageSegment[] => {
  const segments: MessageSegment[] = [];
  let specCallCount = 0;
  let patchInserted = false;

  for (const part of parts) {
    if (part.type === "text" && typeof part.text === "string") {
      const cleaned = stripToolTags(part.text);
      if (!cleaned) continue;
      segments.push({ kind: "text", text: cleaned });
    } else if (isToolPart(part)) {
      const tool: ToolSegmentEntry = {
        toolCallId: part.toolCallId as string,
        toolName: getToolName(part),
        state: part.state as string,
        input: "input" in part ? part.input : undefined,
        output: "output" in part ? part.output : undefined,
      };
      const last = segments[segments.length - 1];
      if (last?.kind === "tools") {
        last.tools.push(tool);
      } else {
        segments.push({ kind: "tools", tools: [tool] });
      }
    } else if (part.type === specPartType) {
      const data = (
        part as { type: string; data?: { type?: string; spec?: unknown } }
      ).data;
      if (data?.type === "flat") {
        // Embed payload directly — no separate lookup needed.
        segments.push({
          kind: "spec",
          spec: data.spec ?? undefined,
          specIndex: specCallCount++,
        });
      } else if (!patchInserted) {
        patchInserted = true;
        const partWithSpec = part as { spec?: unknown; data?: unknown };
        segments.push({
          kind: "spec",
          spec: partWithSpec.spec ?? partWithSpec.data,
        });
      }
    }
  }

  return segments;
};

/**
 * Plain text suitable for “copy answer”: concatenates assistant text segments only
 * (same cleaning as {@link buildMessageSegments}); omits tool/spec segments.
 */
export const getAssistantPlainTextForCopy = (
  parts: ReadonlyArray<MessagePart>,
  specPartType: string,
): string => {
  const chunks: string[] = [];
  for (const segment of buildMessageSegments(parts, specPartType)) {
    if (segment.kind === "text") {
      chunks.push(segment.text);
    }
  }
  return chunks.join("\n\n").trim();
};

/**
 * Converts a PascalCase catalog type to a kebab-case filename slug.
 * @param componentType - Catalog type in PascalCase.
 * @returns Kebab-case slug safe for filenames.
 * @example `AreaChart` → `area-chart`, `MetricGroup` → `metric-group`.
 */
export const filenameSlugForComponentType = (componentType: string): string =>
  componentType
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * Compact UTC timestamp suitable as a filename suffix.
 * @returns Lexicographically sortable token.
 * @example `2024-05-13T12:34:56.789Z` → `20240513T123456Z`.
 */
export const filenameTimestamp = (): string => {
  const iso = new Date().toISOString();
  return iso.replace(/[-:]/g, "").replace(/\.\d+Z$/, "Z");
};
